import { BUTTON_TITLE } from 'src/constants/main';
import { localeStore } from 'src/mobx/localesStore';
import { IMessageModalProps, SuccessMessage, messagesStore } from '~/view/containers/Messages';

/**
 * @param {string} [title] - title text
 * @param {string} [message] - message text
 * @param {callback} [onConfirm] - callback on press confirm
 * @param {callback} [onCancel] - callback on press cancel
 * @param {boolean} [redConfirmBtn] - confirmation button red color(used in some cases to emphasize importance)
 */
export interface IConfirmationMessageProps {
  title?: string;
  message?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  redConfirmBtn?: boolean;
  confirmBtnTxt?: string;
  cancelBtnTxt?: string;
}

type TConfirmationMessage = Pick<
  IMessageModalProps,
  'id' | 'title' | 'message' | 'options' | 'Content' | 'onRequestClose' | 'hideMessage' | 'contentClassName'
>;

const _confirmationMessage = (messageData: TConfirmationMessage) => {
  messagesStore.addMessage({
    contentClassName: 'message__content_small',
    ...messageData,
  });
};

export const confirmZeroPrice = ({ title, message, onConfirm, onCancel }: IConfirmationMessageProps) => {
  _confirmationMessage({
    title: title || localeStore.t('are_you_sure'),
    message: message || localeStore.t('keep_price_at_zero'),
    options: [
      { title: localeStore.t(BUTTON_TITLE.CONFIRM), onPress: onConfirm, stretched: true },
      { title: localeStore.t(BUTTON_TITLE.CANCEL), onPress: onCancel, appearance: 'tertiary', stretched: true },
    ],
  });
};

export const confirmWithTrans = ({ title, message, onConfirm, onCancel }: IConfirmationMessageProps) => {
  _confirmationMessage({
    title: title ? localeStore.t(title) : '',
    message: message ? localeStore.t(message) : '',
    options: [
      { title: localeStore.t(BUTTON_TITLE.CONFIRM), onPress: onConfirm, stretched: true },
      { title: localeStore.t(BUTTON_TITLE.CANCEL), onPress: onCancel, appearance: 'tertiary', stretched: true },
    ],
  });
};

export const leaveWithoutSaving = ({ onConfirm, onCancel }: IConfirmationMessageProps) => {
  _confirmationMessage({
    title: localeStore.t('are_you_sure'),
    message: localeStore.t('leave_without_saving_changes'),
    options: [
      { title: localeStore.t(BUTTON_TITLE.LEAVE), onPress: onConfirm, stretched: true },
      { title: localeStore.t(BUTTON_TITLE.CANCEL), onPress: onCancel, appearance: 'tertiary', stretched: true },
    ],
  });
};

export const confirmDelete = ({ title, message, onConfirm, onCancel, redConfirmBtn, confirmBtnTxt }: IConfirmationMessageProps) => {
  _confirmationMessage({
    title: title || localeStore.t('are_you_sure'),
    message,
    options: [
      {
        title: confirmBtnTxt || localeStore.t(BUTTON_TITLE.DELETE),
        onPress: onConfirm,
        appearance: redConfirmBtn ? 'destructive-primary' : undefined,
        stretched: true,
      },
      { title: localeStore.t(BUTTON_TITLE.CANCEL), onPress: onCancel, appearance: 'tertiary', stretched: true },
    ],
  });
};

export const confirmMessage = ({ title, message, onConfirm, onCancel, confirmBtnTxt, cancelBtnTxt, redConfirmBtn }: IConfirmationMessageProps) => {
  _confirmationMessage({
    title,
    message,
    options: [
      {
        title: confirmBtnTxt ? confirmBtnTxt : localeStore.t(BUTTON_TITLE.YES),
        onPress: onConfirm,
        appearance: redConfirmBtn ? 'destructive-primary' : undefined,
        stretched: true,
      },
      {
        title: cancelBtnTxt ? cancelBtnTxt : localeStore.t(BUTTON_TITLE.NO),
        onPress: onCancel,
        appearance: 'tertiary',
        stretched: true,
      },
    ],
  });
};

export const confirmBranchBusy = ({ title, message, onConfirm, onCancel }: IConfirmationMessageProps) => {
  _confirmationMessage({
    title: title || localeStore.t('are_you_sure'),
    message,
    options: [
      { title: localeStore.t(BUTTON_TITLE.SET_BUSY), onPress: onConfirm, stretched: true },
      { title: localeStore.t(BUTTON_TITLE.CANCEL), onPress: onCancel, appearance: 'tertiary', stretched: true },
    ],
  });
};

export const confirmSuccess = ({ title, message, onConfirm, onCancel }: IConfirmationMessageProps) => {
  _confirmationMessage({
    id: 'success-message',
    title,
    message,
    Content: SuccessMessage,
    onRequestClose: onConfirm || onCancel,
    hideMessage: true,
    contentClassName: 'success-message',
  });
};
export const informationAlert = ({ title, message, onConfirm }: IConfirmationMessageProps) => {
  _confirmationMessage({
    title: title || localeStore.t('are_you_sure'),
    message,
    options: [{ title: localeStore.t(BUTTON_TITLE.YES), onPress: onConfirm, stretched: true }],
  });
};
export const confirmFullRefundAfterDeleteAllProducts = ({ title, message, onConfirm, onCancel }: IConfirmationMessageProps) => {
  _confirmationMessage({
    title: title || localeStore.t('cc_emptycart_dbx_hdr'),
    message,
    options: [
      {
        title: localeStore.t('cc_emptycart_dbx_btn_confirm'),
        onPress: onConfirm,
        stretched: true,
      },
      { title: localeStore.t('cc_emptycart_dbx_btn_cancel'), onPress: onCancel, appearance: 'tertiary', stretched: true },
    ],
  });
};
